import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  BreadcrumbsComponent,
  ButtonComponent,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  IconComponent,
  LabelComponent,
  ModalService,
  NotificationService,
  OptionComponent,
  PageHeaderComponent,
  PlaceholderComponent,
  SelectComponent,
  Table2Component
} from '@pansa/ui';
import { GroupDTO, GroupTypeEnum, GroupUserDTO, RemoveUserFromGroupDTO } from '../../models/group.model';
import { GroupService } from '../../services/group.service';
import { SortState } from '@app/shared/models/sort.model';
import { GroupListDetailsComponent } from '../group-list-details/group-list-details.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilterSortService } from '@app/shared/services/filter-sort.service';

@Component({
  selector: 'rqa-group-list',
  templateUrl: './group-list.component.html',
  styleUrl: './group-list.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    BreadcrumbsComponent,
    ButtonComponent,
    IconComponent,
    Table2Component,
    DividerComponent,
    EmptyStateComponent,
    GroupListDetailsComponent,
    OptionComponent,
    SelectComponent,
    PlaceholderComponent,
    LabelComponent,
    FormFieldComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class GroupListComponent {
  private readonly groupService = inject(GroupService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly filterService = inject<FilterSortService<GroupDTO>>(FilterSortService);
  private readonly notificationService = inject(NotificationService);

  readonly groups = this.groupService.groupsList;

  filtersForm = new FormGroup({
    global: new FormControl(''),
    groupValue: new FormControl('Wszystkie')
  });

  filters = toSignal(this.filtersForm.valueChanges, { initialValue: this.filtersForm.value });

  readonly sort = signal<SortState>({
    key: 'id',
    direction: ''
  });

  filteredGroups = this.filterService.connect({
    data: this.groups,
    filters: this.filters,
    sort: this.sort
  });

  readonly expandedGroups = signal<Set<string>>(new Set());
  readonly groupUsers = signal<Map<string, GroupUserDTO[]>>(new Map());
  readonly loading = signal<boolean>(false);

  groupTypeOptions: string[] = Object.values(GroupTypeEnum);
  trackBy: keyof GroupDTO = 'id';
  expandedRows = new Set<any>();

  onSortChange(sort: SortState) {
    this.sort.set(sort);
  }

  getGroupUsersArray(groupId: string): GroupUserDTO[] {
    return this.groupUsers().get(groupId) || [];
  }

  onEditGroup(group: GroupDTO): void {
    this.router.navigate(['/groups/edit', group.id]);
  }

  onAddGroup(): void {
    this.router.navigate(['/groups/add']);
  }

  onDeleteGroup(group: GroupDTO): void {
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: `Czy chcesz usunąć grupę ${group.name}`,
      subTitle: 'Usunięcie grupy jest trwałe. Nie spowoduje jednak usunięcia kont użytkowników w niej zawartych.',
      submitText: 'Tak, usuń',
      cancelText: 'Anuluj',
      onSubmit: () => this.deleteGroup(group)
    });
  }

  onDeleteUserFromGroup(event: { groupId: string; username: string }): void {
    const groupName = this.groups().find((gr) => gr.id === event.groupId)?.name;
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: `Czy chcesz usunąć uzytkownika ${event.username} z grupy ${groupName}`,
      subTitle:
        'Usunięcie z grupy nie spowoduje usunięcia konta użytkownika, aby to zrobić przejdź do zakładki Użytkownicy',
      submitText: 'Tak, usuń',
      cancelText: 'Anuluj',
      onSubmit: () => this.deleteUserFromGroup(event)
    });
  }

  onToggleRow(row: GroupDTO): void {
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
    }
  }

  isExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  private deleteGroup(group: GroupDTO): void {
    this.groupService.deleteGroup(group.id).subscribe((res) => {
      this.notificationService.success({title: 'Sukces', subTitle: `Usunięto grupę ${group.name}`});
      this.groupService.groupsResource.reload();
    });
  }

  private deleteUserFromGroup(data: RemoveUserFromGroupDTO): void {
    this.groupService.removeUserFromGroup(data).subscribe((res) => {
      this.notificationService.success({title: 'Sukces', subTitle: `Usunięto użytkownika ${data.username} z grupy.`});
    });
  }
}
